import { Fragment, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Slider } from '@mui/material';
import Box from '@mui/material/Box';
import { Divider } from '@mui/material';
import { FormControl, InputLabel, MenuItem, Select, Switch, FormControlLabel, Tooltip } from '@mui/material';
import { Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/** Retorna o label do campo; se existir tooltip, exibe ícone de informação com o texto ao passar o mouse. */
function getLabelWithTooltip(modelField) {
  if (modelField.tooltip) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {modelField.label}
        <Tooltip title={modelField.tooltip} placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'action.active', verticalAlign: 'middle' }} />
        </Tooltip>
      </span>
    );
  }
  return modelField.label;
}

export default function FormDialogEdit(props) {

  if (!props) {
    return <></>;
  }

  const title = props.title ? props.title : "";

  const data = props.jsonInfo.data ? JSON.parse(props.jsonInfo.data) : {};
  const dataModel = props.jsonInfo.model ? JSON.parse(props.jsonInfo.model) : {};
  const dataSaveProperty = props.jsonInfo.saveProperty ? props.jsonInfo.saveProperty : "";
  const dataSavePropertyIsArray = props.jsonInfo.savePropertyIsArray ? props.jsonInfo.savePropertyIsArray : false;
  const dataSaveId = props.jsonInfo.saveId ? props.jsonInfo.saveId : 0;
  const dataReplaces = props.jsonInfo.replaces ? JSON.parse(props.jsonInfo.replaces) : {};
  const dataRelationOptions = props.jsonInfo.relationOptions ? JSON.parse(props.jsonInfo.relationOptions) : {};
  const dataRelationProperties = props.jsonInfo.relationProperties ? JSON.parse(props.jsonInfo.relationProperties) : {};
  /** Grupos de campos que devem manter o mesmo valor: ao alterar um, todos do grupo são atualizados. Ex.: [["p1_estado_civil", "p2_estado_civil"]] */
  const linkedFields = Array.isArray(props.jsonInfo.linkedFields) ? props.jsonInfo.linkedFields : [];

  const isCreation = !dataSaveId;

  const [formData, setFormData] = useState(data);
  const [validationErrors, setValidationErrors] = useState({});

  const evaluateVisibilityCondition = (condition, data) => {
    if (typeof condition === 'boolean') {
      return condition;
    }
    if (typeof condition === 'string') {
      try {
        let expression = condition
          .replace(/\.contains\(/g, '.includes(')
          .replace(/\.startsWith\(/g, '.startsWith(');
        return new Function('formData', `with(formData) { return ${expression}; }`)(data);
      } catch (error) {
        // console.error(`Error evaluating visibility condition "${condition}":`, error);
        return false;
      }
    }
    return true; // Padrão para visível se a condição for indefinida
  };

  const evaluateRequiredCondition = (required, currentData) => {
    if (typeof required === 'boolean') return required;
    if (typeof required === 'string') return evaluateVisibilityCondition(required, currentData);
    return false;
  };

  useEffect(() => {
    const d = data ? { ...data } : {};
    const bem = d.bem_relacionado || '';
    if (
      d.status_doacao === 'doado_com_usufruto' &&
      (String(bem).includes('imovel:') || String(bem).includes('participacoes_societarias:'))
    ) {
      // Regra SP (Dec. 45.837/01 Art. 11 §2): nua-propriedade = ⅔ do valor da liberalidade.
      // Como o campo é readOnly e a regra é fixa por estado, sempre normalizamos — assim
      // cadastros antigos com valor invertido (ex.: 33,33 referente ao usufruto) são corrigidos
      // ao abrir o formulário, e o usuário só precisa salvar para persistir.
      const spNuaPct = 200 / 3;
      const stored = Number(d.percentual_nua_propriedade);
      if (!Number.isFinite(stored) || Math.abs(stored - spNuaPct) > 0.01) {
        d.percentual_nua_propriedade = spNuaPct;
      }
    }
    setFormData(d);
    setValidationErrors({});
  }, [props.open]);

  useEffect(() => {
    let needsUpdate = false;
    const newFormData = { ...formData };

    dataModel.forEach(modelField => {
      if (typeof modelField.visible !== 'string') {
        return;
      }
      
      const isVisible = evaluateVisibilityCondition(modelField.visible, formData);
      
      if (!isVisible) {
        const defaultValue = modelField.defaultValue ?? null;
        
        if (formData[modelField.field] !== defaultValue) {
          newFormData[modelField.field] = defaultValue;
          needsUpdate = true;
        }
      }
    });

    if (needsUpdate) {
      setFormData(newFormData);
    }
  }, [formData, dataModel]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const errors = {};

    dataModel.forEach(modelField => {
      const isVisible = evaluateVisibilityCondition(modelField.visible, formData);
      const isRequired = evaluateRequiredCondition(modelField.required, formData);

      if (isVisible && isRequired) {
        const value = formData[modelField.field];
        
        if (value === null || value === undefined ||
           (typeof value === 'string' && value.trim() === '') ||
           (Array.isArray(value) && value.length === 0) ||
           (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) ||
           ((modelField.type === 'money' || modelField.type === 'number') && value === 0)
        ) {
          errors[modelField.field] = 'Este campo é obrigatório';
        }
      }
    });

    const passwordFields = dataModel
      .filter(field => field.field === 'senha' || field.field === 'senha_confirmacao')
      .map(field => field.field);

    if (passwordFields.length > 1) {
      const firstPasswordValue = formData[passwordFields[0]];
      passwordFields.slice(1).forEach(field => {
        if (formData[field] !== firstPasswordValue) {
          errors[field] = 'As senhas não coincidem';
        }
      });
    }

    if (Object.keys(errors).length > 0) {
      console.error('Validation Errors:', errors);
      setValidationErrors(errors);
      return;
    }

    const payload = { ...formData };
    Object.keys(payload).forEach((k) => {
      if (k.startsWith('_')) delete payload[k];
    });

    const dataToSave = [];
    dataToSave[dataSaveProperty] = dataSavePropertyIsArray ? [payload] : payload;

    const isItemToDelete = false;
    props.onSave(dataToSave, dataSavePropertyIsArray, dataSaveId, isItemToDelete);
    props.onClose();
  };

  const handleChange = (field, value, subField = null) => {
    setFormData((prevData) => {
      setValidationErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[field];
        return newErrors;
      });
      const newData = { ...prevData };

      if (subField) {
        newData[field] = {
          ...newData[field],
          [subField]: value
        };
      } else if (field.includes('-')) {
        const [mainField, subFieldName] = field.split('-');
        newData[mainField] = {
          ...newData[mainField],
          [subFieldName]: value
        };
      } else {
        newData[field] = value;
      }

      // Doação com usufruto (SP): nua = ⅔ do valor da liberalidade — percentual usado no cadastro e no cálculo do espólio
      if (!subField && !field.includes('-')) {
        const spNuaPct = 200 / 3;
        if (field === 'status_doacao' && value === 'doado_com_usufruto') {
          const bem = newData.bem_relacionado || '';
          if (bem.includes('imovel:') || bem.includes('participacoes_societarias:')) {
            newData.percentual_nua_propriedade = spNuaPct;
          }
        }
        if (field === 'bem_relacionado') {
          const tipo = String(value).split(':')[0];
          if (
            newData.status_doacao === 'doado_com_usufruto' &&
            (tipo === 'imovel' || tipo === 'participacoes_societarias')
          ) {
            newData.percentual_nua_propriedade = spNuaPct;
          }
        }
      }

      // Campos vinculados: ao alterar um, todos do mesmo grupo recebem o mesmo valor (para uso sem subField)
      if (!subField && !field.includes('-') && linkedFields.length > 0) {
        for (const group of linkedFields) {
          if (Array.isArray(group) && group.includes(field)) {
            const valueToSync = newData[field];
            for (const otherField of group) {
              if (otherField !== field) newData[otherField] = valueToSync;
            }
            break;
          }
        }
      }

      return newData;
    });
  };

  const renderField = (modelField, dataRelationOptions) => {
    const isFieldPresent = Object.prototype.hasOwnProperty.call(formData, modelField.field);
    const rawValue = isFieldPresent ? formData[modelField.field] : undefined;
    const value = rawValue ?? (modelField.defaultValue ?? '');
    const isVisible = evaluateVisibilityCondition(modelField.visible, formData);
    const isRequired = isVisible && evaluateRequiredCondition(modelField.required, formData);
    if (rawValue === undefined && modelField.defaultValue !== undefined) {
      handleChange(modelField.field, modelField.defaultValue);
    }
    const hasError = !!validationErrors[modelField.field];
    const errorMessage = validationErrors[modelField.field];

    switch (modelField.type) {
      case 'string':
      case 'email':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type={modelField.type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'password':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type="password"
            value={value}
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'longString':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type={'text'}
            value={value}
            multiline={true}
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'number':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type="number"
            value={value}
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'money':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type="text"
            value={value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value)) : ''}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, '');
              const floatValue = numericValue ? Number(parseInt(numericValue) / 100) : null;
              handleChange(modelField.field, floatValue);
            }}
            fullWidth
            inputProps={{
              inputMode: 'numeric',
              placeholder: 'R$ 0,00'
            }}
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'percentage':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            type={modelField.readOnly ? 'text' : 'number'}
            value={
              modelField.readOnly && value !== '' && value != null && !Number.isNaN(Number(value))
                ? (Math.round(Number(value) * 100) / 100).toFixed(2).replace('.', ',')
                : value
            }
            onChange={(e) => {
              if (modelField.readOnly) return;
              handleChange(modelField.field, e.target.value);
            }}
            fullWidth
            InputProps={{ readOnly: Boolean(modelField.readOnly) }}
            error={hasError}
            helperText={hasError ? errorMessage : modelField.readOnly ? 'Preenchido automaticamente (⅔)' : ''}
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                id={modelField.field}
                onChange={(e) => handleChange(modelField.field, e.target.checked)}
              />
            }
            label={getLabelWithTooltip(modelField)}
          />
        );

      case 'date':
        return (
          <TextField
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            value={value}
            type="date"
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            sx={{ width: '100%' }}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              max: '9999-12-31'
            }}
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'options':
        const visibleOptions = modelField.options?.filter(option => {
          if (typeof option !== 'object' || !option.hasOwnProperty('visible')) {
            return true;
          }
          return evaluateVisibilityCondition(option.visible, formData);
        });

        return (
          <FormControl fullWidth required={isRequired} error={hasError}>
            <InputLabel id={`${modelField.field}-label`}>{getLabelWithTooltip(modelField)}</InputLabel>
            <Select
              labelId={`${modelField.field}-label`}
              id={modelField.field}
              value={value}
              label={getLabelWithTooltip(modelField)}
              onChange={(e) => handleChange(modelField.field, e.target.value)}
            >
              {visibleOptions?.map((option, idx) => {
                let label = option.label || option;
                if (dataReplaces && typeof label === 'string') {
                  Object.entries(dataReplaces).forEach(([key, value]) => {
                    label = label.replace(key, value);
                  });
                }
                return (
                  <MenuItem key={option.value || option + idx} value={option.value || option}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
            {hasError && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
          </FormControl>
        );

      case 'bemRelacionado':
        return (
          <FormControl fullWidth required={isRequired} error={hasError}>
            <InputLabel id={`${modelField.field}-label`}>{getLabelWithTooltip(modelField)}</InputLabel>
            <Select
              labelId={`${modelField.field}-label`}
              id={modelField.field}
              value={value}
              label={getLabelWithTooltip(modelField)}
              onChange={(e) => handleChange(modelField.field, e.target.value)}
            >
              {dataRelationProperties?.map((item) => {
                const itemValue = `${item.tipo}:${item.id}`;
                return (
                  <MenuItem key={itemValue} value={itemValue}>
                    {item.tipo_descricao}: {item.descricao} ({item.valor})
                  </MenuItem>
                );
              })}
            </Select>
            {hasError && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
          </FormControl>
        );

      case 'relation':
        const relationValue = (isCreation && !isFieldPresent) ? {} : (value || {});
        return (
          <Grid container columnSpacing={2} marginBottom={0}>
            <Grid item xs={10} md={5}>
              <FormControl fullWidth required={isRequired} error={hasError}>
                <InputLabel id={`${modelField.field}-tipo-label`}>{getLabelWithTooltip(modelField)}</InputLabel>
                <Select
                  labelId={`${modelField.field}-tipo-label`}
                  id={`${modelField.field}-tipo`}
                  value={relationValue.tipo || ''}
                  label={getLabelWithTooltip(modelField)}
                  onChange={(e) => handleChange(modelField.field, { ...relationValue, tipo: e.target.value })}
                >
                  {modelField.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {hasError && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={4} md={1} marginTop={2}>
              <>De</>
            </Grid>

            <Grid item xs={10} md={5}>
              <FormControl fullWidth required={isRequired} error={hasError}>
                <InputLabel id={`${modelField.field}-id-label`}>Pessoa</InputLabel>
                <Select
                  labelId={`${modelField.field}-id-label`}
                  id={`${modelField.field}-id`}
                  value={relationValue.id || ''}
                  label='Pessoa'
                  onChange={(e) => handleChange(modelField.field, { ...relationValue, id: e.target.value })}
                >
                  {dataRelationOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.nome}
                    </MenuItem>
                  ))}
                </Select>
                {hasError && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
              </FormControl>
            </Grid>
          </Grid>
        );

      case 'relationId':
        const relationIdOptions = Array.isArray(dataRelationOptions) ? dataRelationOptions.filter((x) => x.id !== -1 && x.id !== '-1') : [];
        const relationIdValue = value !== undefined && value !== null && value !== '' ? value : '';
        const relationIdValueStr = relationIdValue === '' ? '' : String(relationIdValue);
        return (
          <FormControl fullWidth required={isRequired} error={hasError}>
            <InputLabel id={`${modelField.field}-label`}>{getLabelWithTooltip(modelField)}</InputLabel>
            <Select
              labelId={`${modelField.field}-label`}
              id={modelField.field}
              value={relationIdValueStr}
              label={getLabelWithTooltip(modelField)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  handleChange(modelField.field, null);
                  return;
                }
                const opt = relationIdOptions.find((o) => String(o.id) === String(v));
                handleChange(modelField.field, opt != null ? opt.id : v);
              }}
            >
              {relationIdOptions.map((option) => (
                <MenuItem key={String(option.id)} value={String(option.id)}>
                  {option.nome}
                </MenuItem>
              ))}
            </Select>
            {hasError && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
          </FormControl>
        );

      case 'list':
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={(e) => handleChange(modelField.field, e.target.value.split(',').map(item => item.trim()))}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : "Digite valores separado por vírgula"}
          />
        );

      case 'address':
        const addressValue = (isCreation && !isFieldPresent) ? {} : (value || {});
        return (
          <Grid container columnSpacing={2} marginBottom={0}>
            <Grid item xs={12} md={6}>
              <TextField
                required={isRequired}
                id={`${modelField.field}-rua`}
                label="Rua"
                value={addressValue.rua || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'rua')}
                fullWidth
                error={hasError && !addressValue.rua}
                helperText={hasError && !addressValue.rua ? errorMessage : ''}
              />
              <FormControl fullWidth required={isRequired} error={hasError && !addressValue.estado}>
                <InputLabel id={`${modelField.field}-estado-label`}>Estado</InputLabel>
                <Select
                  labelId={`${modelField.field}-estado-label`}
                  id={`${modelField.field}-estado`}
                  value={addressValue.estado || ''}
                  label="Estado"
                  onChange={(e) => handleChange(modelField.field, e.target.value, 'estado')}
                >
                  <MenuItem value="AC">Acre</MenuItem>
                  <MenuItem value="AL">Alagoas</MenuItem>
                  <MenuItem value="AP">Amapá</MenuItem>
                  <MenuItem value="AM">Amazonas</MenuItem>
                  <MenuItem value="BA">Bahia</MenuItem>
                  <MenuItem value="CE">Ceará</MenuItem>
                  <MenuItem value="DF">Distrito Federal</MenuItem>
                  <MenuItem value="ES">Espírito Santo</MenuItem>
                  <MenuItem value="GO">Goiás</MenuItem>
                  <MenuItem value="MA">Maranhão</MenuItem>
                  <MenuItem value="MT">Mato Grosso</MenuItem>
                  <MenuItem value="MS">Mato Grosso do Sul</MenuItem>
                  <MenuItem value="MG">Minas Gerais</MenuItem>
                  <MenuItem value="PA">Pará</MenuItem>
                  <MenuItem value="PB">Paraíba</MenuItem>
                  <MenuItem value="PR">Paraná</MenuItem>
                  <MenuItem value="PE">Pernambuco</MenuItem>
                  <MenuItem value="PI">Piauí</MenuItem>
                  <MenuItem value="RJ">Rio de Janeiro</MenuItem>
                  <MenuItem value="RN">Rio Grande do Norte</MenuItem>
                  <MenuItem value="RS">Rio Grande do Sul</MenuItem>
                  <MenuItem value="RO">Rondônia</MenuItem>
                  <MenuItem value="RR">Roraima</MenuItem>
                  <MenuItem value="SC">Santa Catarina</MenuItem>
                  <MenuItem value="SP">São Paulo</MenuItem>
                  <MenuItem value="SE">Sergipe</MenuItem>
                  <MenuItem value="TO">Tocantins</MenuItem>
                  <MenuItem value="Internacional">Não se Aplica - Internacional</MenuItem>
                </Select>
                {hasError && !addressValue.estado && <Typography color="error" style={{ textTransform: 'inherit', fontWeight: 'bold', fontSize: '10px !important', marginTop: '4px' }}>{errorMessage}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required={isRequired}
                id={`${modelField.field}-cidade`}
                label="Cidade"
                value={addressValue.cidade || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'cidade')}
                fullWidth
                error={hasError && !addressValue.cidade}
                helperText={hasError && !addressValue.cidade ? errorMessage : ''}
              />
              <TextField
                required={isRequired}
                id={`${modelField.field}-cep`}
                label="CEP"
                value={addressValue.cep || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'cep')}
                fullWidth
                error={hasError && !addressValue.cep}
                helperText={hasError && !addressValue.cep ? errorMessage : ''}
              />
              <TextField
                required={isRequired}
                id={`${modelField.field}-pais`}
                label="País"
                value="Brasil"
                fullWidth
                disabled
                style={{ display: 'none' }}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'pais')}
              />
            </Grid>
          </Grid>
        );

      case 'doacaoPessoaInventario':
        const doacaoValue = Array.isArray(value) ? value : [];
        const totalPercentage = doacaoValue.reduce((acc, curr) => acc + (Number(curr.porcentagem_doacao) || 0), 0);

        const handleDonationChange = (personId, newPercentage) => {
          const newPercentageNum = Math.round(Math.max(0, Math.min(100, Number(newPercentage))));
          let newDoacaoValue = JSON.parse(JSON.stringify(doacaoValue));
          const currentIndex = newDoacaoValue.findIndex(d => d.pessoa_inventario_id === personId);

          if (currentIndex !== -1) {
            newDoacaoValue[currentIndex].porcentagem_doacao = newPercentageNum;
          } else {
            newDoacaoValue.push({ pessoa_inventario_id: personId, porcentagem_doacao: newPercentageNum });
          }

          const currentTotal = newDoacaoValue.reduce((acc, d) => acc + (d.porcentagem_doacao || 0), 0);

          if (currentTotal > 100) {
            const overflow = currentTotal - 100;
            const otherDonations = newDoacaoValue.filter(d => d.pessoa_inventario_id !== personId && d.porcentagem_doacao > 0);
            let totalOtherPercentage = otherDonations.reduce((acc, d) => acc + d.porcentagem_doacao, 0);

            if (totalOtherPercentage > 0) {
              for (let i = 0; i < newDoacaoValue.length; i++) {
                if (newDoacaoValue[i].pessoa_inventario_id !== personId && newDoacaoValue[i].porcentagem_doacao > 0) {
                  const proportion = newDoacaoValue[i].porcentagem_doacao / totalOtherPercentage;
                  const adjustment = overflow * proportion;
                  newDoacaoValue[i].porcentagem_doacao = Math.round(Math.max(0, newDoacaoValue[i].porcentagem_doacao - adjustment));
                }
              }
            }
          }
          handleChange(modelField.field, newDoacaoValue);
        };

        return (
          <Grid container columnSpacing={2} rowSpacing={3} alignItems="center">
            {dataRelationOptions.filter(x => x.id !== -1).map((option) => {
              const pessoaDoacaoValue = doacaoValue.find(d => d.pessoa_inventario_id === option.id) || {};
              const currentPercentage = Math.round(Number(pessoaDoacaoValue.porcentagem_doacao) || 0);

              return (
                <Fragment key={option.id}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      value={option.nome}
                      label="Beneficiário"
                      type="text"
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField
                      required={isRequired}
                      id={`${modelField.field}-porcentagem-${option.id}`}
                      label="%"
                      type="number"
                      value={currentPercentage}
                      onChange={(e) => {
                        handleDonationChange(option.id, e.target.value);
                      }}
                      fullWidth
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      error={hasError && !currentPercentage}
                    />
                  </Grid>
                  <Grid item xs={8} md={5}>
                    <Slider
                      value={currentPercentage}
                      onChange={(e, newValue) => handleDonationChange(option.id, newValue)}
                      aria-labelledby="input-slider"
                      min={0}
                      max={100}
                      step={1}
                    />
                  </Grid>
                </Fragment>
              );
            })}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" style={{ color: totalPercentage > 100 ? 'red' : 'inherit' }}>
                Total: {Math.round(totalPercentage)}%
              </Typography>
              {totalPercentage > 100 && (
                <Typography color="error" variant="caption">
                  A soma das porcentagens não pode exceder 100%.
                </Typography>
              )}
            </Grid>
          </Grid>
        );

      case 'doacaoUsufrutoSpSplit': {
        const v = Number(formData.valor) || 0;
        const fmt = (n) =>
          new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
        const nua = v * (2 / 3);
        const usu = v * (1 / 3);
        return (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Distribuição do valor atribuído (SP — liberalidade com usufruto)
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Nua-propriedade (⅔ · 66,67%): <strong>{fmt(nua)}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Usufruto (⅓ · 33,33%): <strong>{fmt(usu)}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Conforme proporção usual na tabela de ITCMD de São Paulo (Dec. 45.837/2002, art. 11, §2º).
            </Typography>
          </Box>
        );
      }

      case 'internacionalAddress':
        const internationalAddressValue = (isCreation && !isFieldPresent) ? {} : (value || {});
        return (
          <Grid container columnSpacing={2} marginBottom={0}>
            <Grid item xs={12} md={6}>
              <TextField
                required={isRequired}
                id={`${modelField.field}-rua`}
                label="Rua"
                value={internationalAddressValue.rua || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'rua')}
                fullWidth
                error={hasError && !internationalAddressValue.rua}
                helperText={hasError && !internationalAddressValue.rua ? errorMessage : ''}
              />
              <TextField
                required={isRequired}
                id={`${modelField.field}-cidade`}
                label="Cidade"
                value={internationalAddressValue.cidade || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'cidade')}
                fullWidth
                error={hasError && !internationalAddressValue.cidade}
                helperText={hasError && !internationalAddressValue.cidade ? errorMessage : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required={isRequired}
                id={`${modelField.field}-estado`}
                label="Estado"
                value={internationalAddressValue.estado || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'estado')}
                fullWidth
                error={hasError && !internationalAddressValue.estado}
                helperText={hasError && !internationalAddressValue.estado ? errorMessage : ''}
              />
              <TextField
                required={isRequired}
                id={`${modelField.field}-pais`}
                label="País"
                value={internationalAddressValue.pais || ''}
                onChange={(e) => handleChange(modelField.field, e.target.value, 'pais')}
                fullWidth
                error={hasError && !internationalAddressValue.pais}
                helperText={hasError && !internationalAddressValue.pais ? errorMessage : ''}
              />
            </Grid>
          </Grid>
        );

      case 'separator':
        return (
          <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
            <Typography variant="h5" gutterBottom component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {getLabelWithTooltip(modelField)}
            </Typography>
            {modelField.description && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {modelField.description}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            required={isRequired}
            id={modelField.field}
            label={getLabelWithTooltip(modelField)}
            value={value}
            onChange={(e) => handleChange(modelField.field, e.target.value)}
            fullWidth
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );
    }
  };

  const evaluateVisibility = (modelField) => {
    const isVisible = evaluateVisibilityCondition(modelField.visible, formData);
    return { display: isVisible ? 'block' : 'none' };
  };

  return (
    <Fragment>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
          noValidate: true,
        }}
        maxWidth="md"
        sx={{ zIndex: 1000 }}
        fullWidth
      >
        <DialogTitle component="div" sx={{ mb: -1.5 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1.5rem' }}>
            {title}
          </Typography>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Grid container columnSpacing={2}>
            {dataModel.map((modelField, idx) => (
              (
                <Grid item md={modelField.sizeMD} sm={modelField.sizeSM} xs={modelField.sizeSM} key={idx}
                  sx={{
                    display: evaluateVisibility(modelField).display,
                  }}
                >
                  <Box
                    sx={{
                      '& > *': { mb: 3 },
                      '& .MuiTextField-root': { width: '100%', mb: 2 },
                      '& .MuiFormControl-root': { mb: 2 },
                      '& .MuiFormControlLabel-root': { mb: 2 },
                      pt: 1,
                      display: evaluateVisibility(modelField).display,
                    }}
                  >
                    {renderField(modelField, dataRelationOptions)}
                  </Box>
                </Grid>
              )
            ))}
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions>
          <Button type="button" onClick={props.onClose}>Cancelar</Button>
          <Button variant="contained" type="submit">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
